/* Showcase Driver App — the 5-second phone cameo after the Batcher Cockpit on the public
   vault entrance. A replica of the real driver PWA in a phone frame: the docket, slump,
   W/C check and the tap-through steps (Depart Plant → Arrived On Site → Start Discharge →
   Docket Signed). Simulated client-side; no auth, no API, no real docket data. */
import { useEffect, useRef, useState } from "react";
import { vol, slump, mixLabel } from "@/lib/showcaseUnits";
import { SITES } from "@/lib/showcaseCity";
import { Navigation, MapPin, Droplets, Flag, Phone, Check, Wifi, Info, ChevronRight } from "lucide-react";

const ORANGE = "#F57C00";
const STEPS = [
  { key: "en_route", label: "Depart Plant", sub: "Leaving the batch plant", Icon: Navigation, at: 900, status: "EN ROUTE" },
  { key: "on_site", label: "Arrived On Site", sub: "At the customer's site", Icon: MapPin, at: 2100, status: "ON SITE" },
  { key: "discharging", label: "Start Discharge", sub: "Pouring has started", Icon: Droplets, at: 3200, status: "DISCHARGING" },
  { key: "sign", label: "Finish · Docket Signed", sub: "Customer signs the docket", Icon: Flag, at: 4300, status: "DELIVERED" },
];

export default function ShowcaseDriver() {
  const [ms, setMs] = useState(0);
  const start = useRef(Date.now());
  useEffect(() => {
    const i = setInterval(() => setMs(Date.now() - start.current), 80);
    return () => clearInterval(i);
  }, []);

  const doneIdx = STEPS.reduce((acc, s, i) => (ms >= s.at ? i : acc), -1);
  const status = doneIdx >= 0 ? STEPS[doneIdx].status : "LOADED · AT PLANT";
  const nextIdx = Math.min(STEPS.length - 1, doneIdx + 1);
  const tapping = ms >= (STEPS[nextIdx]?.at ?? 1e9) - 260 && ms < (STEPS[nextIdx]?.at ?? 0);
  const measured = ms > 700 ? slump(80).v : null;
  const signed = doneIdx >= 3;

  return (
    <div className="absolute inset-0 overflow-hidden select-none flex items-center justify-center" data-testid="showcase-driver"
      style={{ background: "radial-gradient(circle at 50% 35%, #1C1F24 0%, #07080A 70%)" }}>
      {/* caption */}
      <div className="absolute top-6 left-0 right-0 text-center px-6">
        <div className="mono text-[9px] tracking-[0.4em] uppercase text-zinc-500">In the truck</div>
        <div className="heading text-lg font-black !text-white mt-1">DRIVER APP · SP-11</div>
        <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
          Works offline · docket signed on the phone · GPS straight to the war room
        </div>
      </div>

      {/* phone */}
      <div className="relative" style={{ width: 330, maxWidth: "88vw" }}>
        <div className="rounded-[2.2rem] border-[10px] border-zinc-800 bg-black shadow-[0_40px_90px_-20px_rgba(0,0,0,.9)] overflow-hidden"
          style={{ height: "min(72vh, 620px)" }}>
          <div className="h-full w-full bg-white text-[#0f1419] flex flex-col overflow-hidden">
            {/* header */}
            <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
              <div className="min-w-0">
                <div className="mono text-[10px] uppercase tracking-widest font-bold" style={{ color: ORANGE }}>ARMORBATCH DRIVER</div>
                <div className="heading text-base font-black truncate">J. Petrie · SP-11</div>
              </div>
              <span className="ml-auto shrink-0 inline-flex items-center gap-1.5 mono text-[10px] font-bold tracking-widest px-2.5 py-1.5 rounded-full bg-emerald-100 text-emerald-700">
                <Wifi className="w-3 h-3" /> SYNCED
                <span className="inline-flex items-center gap-0.5 ml-1 pl-1.5 border-l border-emerald-300"><Navigation className="w-3 h-3" /> GPS</span>
              </span>
            </div>

            <div className="flex-1 overflow-hidden p-3 space-y-2.5">
              {/* docket */}
              <section className="border-2 border-gray-100 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="mono text-[10px] uppercase tracking-widest text-gray-400">Docket D260926-0151</div>
                    <div className="heading text-lg font-black truncate">Hargraves Civil</div>
                    <div className="text-[11px] text-gray-500 truncate">Lot 22 Ocean Pde, {SITES[1].name}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="heading text-2xl font-black" style={{ color: ORANGE }}>{vol(7.5).v.toFixed(1)}<span className="text-xs ml-0.5">{vol(7.5).u}</span></div>
                    <div className="mono text-[10px] uppercase text-gray-500">{mixLabel("N32-20", 32, 20)}</div>
                  </div>
                </div>
                <div className="flex gap-2 mt-2.5">
                  <span className="flex-1 inline-flex items-center justify-center gap-2 border-2 border-gray-200 py-2 text-[12px] font-bold"><Phone className="w-3.5 h-3.5" /> Call site</span>
                  <span className="flex-1 inline-flex items-center justify-center gap-2 border-2 border-gray-200 py-2 text-[12px] font-bold"><Navigation className="w-3.5 h-3.5" /> Directions</span>
                </div>
              </section>

              {/* site instructions */}
              <section className="border-2 border-amber-400 bg-amber-50 p-2.5">
                <div className="mono text-[10px] uppercase tracking-widest text-amber-700 font-black flex items-center gap-1.5">
                  <Info className="w-3 h-3" /> Site instructions
                </div>
                <div className="text-[12px] text-gray-900 mt-1 leading-snug">Enter via the rear gate. Pump on the north side — watch the low wires.</div>
              </section>

              {/* slump + W/C */}
              <div className="grid grid-cols-2 gap-2.5">
                <section className="border-2 border-gray-100 p-2.5">
                  <div className="mono text-[10px] uppercase tracking-widest text-gray-400">Slump</div>
                  <div className={`heading text-2xl font-black ${measured == null ? "text-gray-300" : "text-emerald-600"}`}>
                    {measured ?? "--"}<span className="text-xs ml-0.5">{slump(80).u}</span>
                  </div>
                  <div className={`mono text-[9px] uppercase font-bold ${measured == null ? "text-gray-400" : "text-emerald-600"}`}>
                    {measured == null ? "not checked" : "on target"}
                  </div>
                </section>
                <section className="border-2 border-gray-100 p-2.5">
                  <div className="mono text-[10px] uppercase tracking-widest text-gray-400 flex items-center gap-1"><Droplets className="w-3 h-3" /> W/C</div>
                  <div className="heading text-2xl font-black text-emerald-600">0.52</div>
                  <div className="mono text-[9px] uppercase font-bold text-emerald-600">W/C OK · max 0.55</div>
                </section>
              </div>

              {/* status */}
              <section className="border-2 border-gray-100 p-2.5">
                <div className="mono text-[10px] uppercase tracking-widest text-gray-400">Current status</div>
                <div className="heading text-xl font-black" style={{ color: signed ? "#059669" : ORANGE }} data-testid="showcase-driver-status">
                  {status}
                </div>
              </section>

              {/* steps */}
              <section className="space-y-1.5">
                {STEPS.map((s, i) => {
                  const done = i <= doneIdx;
                  const isNext = i === doneIdx + 1;
                  const flash = isNext && tapping;
                  return (
                    <div key={s.key}
                      className={`w-full flex items-center gap-2.5 border-2 px-2.5 py-2 transition-all duration-200 ${
                        done ? "border-emerald-500 bg-emerald-50" : isNext ? "border-[#F57C00]" : "border-gray-200 opacity-45"}`}
                      style={flash ? { transform: "scale(0.97)", background: "#FFF3E0" } : undefined}>
                      {done ? <Check className="w-4 h-4 text-emerald-600 shrink-0" strokeWidth={3} />
                        : <s.Icon className="w-4 h-4 shrink-0" style={{ color: isNext ? ORANGE : "#9CA3AF" }} />}
                      <div className="min-w-0">
                        <div className="mono text-[9px] uppercase tracking-widest text-gray-400">Step {i + 1}</div>
                        <div className={`text-[13px] font-black leading-tight ${done ? "text-emerald-700" : ""}`}>{s.label}</div>
                        <div className="text-[10px] text-gray-500 leading-tight">{s.sub}</div>
                      </div>
                      {isNext && <ChevronRight className="w-4 h-4 ml-auto shrink-0" style={{ color: ORANGE }} />}
                    </div>
                  );
                })}
              </section>
            </div>

            {signed && (
              <div className="border-t-2 border-emerald-500 bg-emerald-50 px-4 py-2.5 flex items-center gap-2">
                <Check className="w-4 h-4 text-emerald-600" strokeWidth={3} />
                <div className="mono text-[10px] uppercase tracking-widest font-black text-emerald-700">Docket signed · emailed to customer</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
